import useVuelidate from '@vuelidate/core';
import { computed, ref } from 'vue';
import { Learner, LearnerWithInstructorNotes } from '../models/learner';
import { appValidator } from '../utils/validator';

/**
 * User learners select validation.
 * Handle field input, validation and more.
 * @param {object} params Params.
 * @param {import('vue').Ref<boolean> | undefined} [params.isRbsServerIdRequired] Whether RBS server ID is required.
 * @param {import('vue').Ref<boolean> | undefined} [params.isCourseRequiredDateOfBirth] Whether course required date of birth.
 * @param {import('vue').Ref<boolean> | undefined} [params.isAddLearner] Whether is in add learner mode.
 * @param {import('vue').Ref<boolean> | undefined} [params.shouldKeepCurrentValue] Whether current value should be kept.
 * @param {import('vue').Ref<boolean> | undefined} [params.isEmployeeIdRequired] Whether employee ID is required.
 */
export function useLearnersSelectValidation({
  isRbsServerIdRequired,
  isCourseRequiredDateOfBirth,
  isAddLearner,
  shouldKeepCurrentValue,
  isEmployeeIdRequired,
}) {
  const {
    externalServerValidation, requiredWithShortMessage, email, validDateOfBirth,
  } = appValidator;

  /** @type {import('vue').Ref<LearnerWithInstructorNotes[]>} */
  const clonedSelectedLearners = ref([]);

  const validators = ref([]);
  const $externalResults = ref({});

  const rules = computed(() => {
    let validationFields = {};
    if (isAddLearner?.value) {
      validationFields = {
        ...validationFields,
        lastName: {
          requiredWithShortMessage,
        },
        firstName: {
          requiredWithShortMessage,
        },
        email: {
          requiredWithShortMessage,
          email,
        },
      };
    }
    if (isRbsServerIdRequired?.value) {
      validationFields = {
        ...validationFields,
        lastName: {
          requiredWithShortMessage,
        },
        rbsServerId: {
          requiredWithShortMessage,
          ...externalServerValidation,
        },
      };
    }
    if (isCourseRequiredDateOfBirth?.value) {
      validationFields = {
        ...validationFields,
        dateOfBirth: {
          requiredWithShortMessage,
          validDateOfBirth,
        },
      };
    }
    if (isEmployeeIdRequired?.value) {
      validationFields = {
        ...validationFields,
        employeeId: {
          requiredWithShortMessage,
        },
      };
    }
    return validationFields;
  });

  /**
   * Get validator for fields.
   * @param {string} key Key of field.
   * @param {number} id Validator ID.
   * @returns {object | null}
   */
  function getValidatorsForField(key, id) {
    const validator = validators.value.find(v => v.id === id);
    if (validator == null) {
      return null;
    }
    const validatorForField = validator.validator[key];
    return {
      errors: validatorForField?.$errors.map(e => e.$message),
      touch: validatorForField?.$touch,
    };
  }

  /**
   * Get selected learner for row model.
   * @param {number} learnerId Learner ID.
   * @returns {object}
   */
  function getSelectedLearnerForRowById(learnerId) {
    return clonedSelectedLearners.value.find(l => l.id === learnerId) ?? {};
  }

  /**
   * Check if learner in selected learner list.
   * @param {Learner} learner Learner to check.
   * @returns {boolean}
   */
  function isInSelectedList(learner) {
    return clonedSelectedLearners.value.find(l => l.id === learner.id) != null;
  }

  /**
   * Validate form.
   * @param {Array} validatorsToExecute Validators.
   */
  function touchForm(validatorsToExecute) {
    validatorsToExecute.forEach(v => {
      v.validator.$touch();
    });
  }

  /**
   * Clear validate result.
   * @param {number} learnerIdToClear learner ID to clear.
   */
  function clearServerError(learnerIdToClear) {
    $externalResults.value[learnerIdToClear] = null;
  }

  /**
   * Create validators for selected learners.
   * @param {object} params Params
   * @param {LearnerWithInstructorNotes[]} params.newSelectedLearners Selected learners.
   * @param {boolean} [params.shouldKeepOldValidator] Whether old validator should be kept.
   * @param {boolean} [params.shouldResetValidator] Should reset validator.
   */
  function createValidatorForClonedSelectedLearners({ newSelectedLearners, shouldKeepOldValidator, shouldResetValidator }) {
    // Keep in mind that `useVuelidate` only work with reactive object or array, do not pass a plain object without reactivity
    // into this function.

    newSelectedLearners.forEach(learner => {
      $externalResults.value[learner.id] = null;
    });

    const newValidators = newSelectedLearners.map(learner => {
      const newValidator = {
        id: learner.id,
        validator: useVuelidate(
          rules,
          learner,
        ),
      };
      if (shouldResetValidator) {
        return newValidator;
      }
      if (shouldKeepOldValidator) {
        const existCurrentValidator = validators.value.find(v => v.id === learner.id);
        return existCurrentValidator ?? newValidator;
      }
      return newValidator;
    });
    validators.value = newValidators;
  }

  /**
   * Check if value is empty.
   * @param {unknown} valueToCheck Value to check.
   * @returns {boolean}
   */
  function isEmpty(valueToCheck) {
    return valueToCheck == null || valueToCheck === '';
  }

  /** Reset cloned learners list. */
  function resetClonedLearnersList() {
    clonedSelectedLearners.value = [];
  }

  /**
   * We need to clone `selectedLearners` into a new `ref` in order to not mutate directly in `selectedLearners`.
   * If we mutate `selectedLearners`, the original value of table will also change.
   * @param {object} params Params
   * @param {LearnerWithInstructorNotes[]} params.newSelectedLearners Selected learners.
   * @param {boolean} [params.shouldKeepOldValidator] Whether old validator should be kept.
   * @param {boolean} [params.shouldResetValidator] Is reset.
   */
  function onSelectedLearnersChange({ newSelectedLearners, shouldKeepOldValidator = false, shouldResetValidator = false }) {
    // Clear external results.
    $externalResults.value = {};

    if (!shouldKeepOldValidator) {
      // Cloning selectedLearners.
      let clonedLearners = newSelectedLearners.map(v => ({ ...v, instructorNotes: '' }));

      clonedLearners = clonedLearners.map(v => {
        const currentClonedLearner = clonedSelectedLearners.value.find(learner => learner.id === v.id);
        if (shouldKeepCurrentValue?.value != null && !shouldKeepCurrentValue?.value) {
          return {
            ...v,
            instructorNotes: currentClonedLearner?.instructorNotes ?? '',
          };
        }
        return {
          ...v,
          // Use for PFS admin view and package include class product.
          instructorNotes: currentClonedLearner?.instructorNotes ?? '',

          firstName: currentClonedLearner?.firstName ?? v.firstName,
          email: currentClonedLearner?.email ?? v.email,

          // Use for RBS package, which is CALIFORNIA_RESPONSIBLE_BEVERAGE_SERVICE_TRAINING_ID, both GA and PFS admin.
          lastName: currentClonedLearner?.lastName ?? v.lastName,
          rbsServerId: currentClonedLearner?.rbsServerId,

          // Use for courses with need to collect DOB, defined with COURSES_REQUIRED_DOB.
          dateOfBirth: currentClonedLearner?.dateOfBirth ?? v.dateOfBirth,

          // Use for pandargca corporate manager.
          employeeId: currentClonedLearner?.employeeId ?? v.employeeId ?? '',
        };
      });
      clonedSelectedLearners.value = clonedLearners;
    } else {
      clonedSelectedLearners.value = newSelectedLearners;
    }

    // Need to pass `clonedSelectedLearners.value` to `createValidatorForClonedSelectedLearners` to ensure the reactivity.
    // We cannot pass `clonedLearners` since this array does not include reactivity.
    createValidatorForClonedSelectedLearners({
      newSelectedLearners: clonedSelectedLearners.value,
      shouldKeepOldValidator,
      shouldResetValidator,
    });
  }

  /**
   * Validate form.
   * @param {(learnersValidated: LearnerWithInstructorNotes[]) => any} callbackExecute Callback to execute.
   * @returns {any}
   */
  function validateFormAndExecute(callbackExecute) {
    touchForm(validators.value);
    const isFormInvalid = validators.value.find(validator => validator.validator.$invalid) != null;
    if (isFormInvalid) {
      return;
    }
    return callbackExecute(clonedSelectedLearners.value);
  }

  /**
   * Set external value.
   * @param {number} learnerId Learner ID.
   * @param {object} error Error.
   */
  function setServerError(learnerId, error) {
    $externalResults.value[learnerId] = error;
  }

  /**
   * Validate form with learner has value inputted.
   * @returns {LearnerWithInstructorNotes[]}
   */
  function validateOnlyInputtedForm() {
    const learnersToValidate = clonedSelectedLearners.value.filter(
      learner => !isEmpty(learner.firstName)
      || !isEmpty(learner.lastName) || !isEmpty(learner.dateOfBirth)
      || !isEmpty(learner.email) || !isEmpty(learner.rbsServerId)
      || !isEmpty(learner.employeeId),
    );
    const validatorsToExecute = validators.value.filter(v => learnersToValidate.find(l => l.id === v.id) != null);
    touchForm(validatorsToExecute);
    const isFormInvalid = validatorsToExecute.find(validator => validator.validator.$invalid) != null;
    if (!isFormInvalid) {
      return learnersToValidate;
    }
    return [];
  }

  return {
    serverError: $externalResults,

    resetClonedLearnersList,
    setServerError,
    clearServerError,
    getValidatorsForField,
    getSelectedLearnerForRowById,
    isInSelectedList,
    onSelectedLearnersChange,
    validateFormAndExecute,
    createValidatorForClonedSelectedLearners,
    validateOnlyInputtedForm,
  };
}
